import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { FilterMetadata, FilterOperator, SortMeta } from 'primeng/api';
import { Table } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';
import { environment } from 'src/environments/environment';
import { AccountComponent } from '../account/account.component';
import { AuthRoles } from '../auth/auth.types';
import { DateRangeFilterModel } from './models/date-range-filter-model';
import { GeographicFilterModel } from './models/geographic-filter-model';

export class AppUtils {

  public static sanitizeJsonObject(sanitizer: DomSanitizer, jsonObj: any): string {
    return sanitizer.sanitize(SecurityContext.HTML, JSON.stringify(jsonObj)).replace(/\&\#34;/g, "\"");
  }

  public static base64toBlob(base64Data: string, contentType: string): Blob {
    contentType = contentType || '';
    let sliceSize = 1024;
    let byteCharacters = atob(base64Data);
    let bytesLength = byteCharacters.length;
    let slicesCount = Math.ceil(bytesLength / sliceSize);
    let byteArrays = new Array(slicesCount);
    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      let begin = sliceIndex * sliceSize;
      let end = Math.min(begin + sliceSize, bytesLength);

      let bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  public static createCSVString(options: any): string {
    let csvString: string = '';
    let data = options.data || options.dataTable?.filteredValue || options.dataTable?.value;
    if (options && options.selectionOnly) {
      data = options.dataTable.selection || [];
    }

    //headers
    for (let i = 0; i < options.columns.length; i++) {
      let column = options.columns[i];
      if (column.exportable !== false && column.field) {
        csvString += '"' + (column.header || column.field) + '"';

        if (i < (options.columns.length - 1)) {
          csvString += options.csvSeparator;
        }
      }
    }

    //body
    data.forEach((record: any, i: number) => {
      csvString += '\n';
      for (let i = 0; i < options.columns.length; i++) {
        let column = options.columns[i];
        if (column.exportable !== false && column.field) {
          let cellData = ObjectUtils.resolveFieldData(record, column.field);

          if (cellData != null) {
            cellData = this.escapeCSVInjection(cellData);
            if (options.exportFunction) {
              cellData = options.exportFunction({
                data: cellData,
                field: column.field
              });
            }
            else
              cellData = String(cellData).replace(/"/g, '""');
          }
          else
            cellData = '';


          csvString += '"' + cellData + '"';

          if (i < (options.columns.length - 1)) {
            csvString += options.csvSeparator;
          }
        }
      }
    });

    return csvString;
  }

  public static escapeCSVInjection(cellData: any): string {
    cellData = cellData.toString();
    if (cellData.startsWith('-') || cellData.startsWith('@') || cellData.startsWith('=') || cellData.startsWith('+')) {
      cellData = ' ' + cellData;
    }
    return cellData;
  }

  public static createDateRangeFilterModel(storageKey: string, duration: number): DateRangeFilterModel {
    let fm: any;
    if (fm = JSON.parse(sessionStorage.getItem(storageKey))) {
      return new DateRangeFilterModel(fm._cgicCode, fm._duration, fm._startDate, fm._endDate);
    } else {
      let filterModel: DateRangeFilterModel = new DateRangeFilterModel(AppUtils.userFieldDivisionID(), duration);
      sessionStorage.setItem(storageKey, JSON.stringify(filterModel));
      return filterModel;
    }
  }

  public static createGlobalFilterModel(storageKey: string, fieldDivisionGroupID: string, fieldDivisionGroups: ServerModels.FieldDivisionGroupViewModel[], stateID: string, countyID: string, counties: ServerModels.ENESS_REF_Counties[], agencyID: string, agencies: ServerModels.ENESS_REF_Agency[], fieldDivisionName: string, groupName: string, groupNameList: string, stateName: string, countyName: string, countyNameList: string, agencyName: string, agencyNamesList: string): GeographicFilterModel {
    let fm: any;
    if (fm = JSON.parse(sessionStorage.getItem(storageKey))) {
      return new GeographicFilterModel(fm._fieldDivisionID, fm._fieldDivisionGroupID, fm._fieldDivisionGroups, fm._stateID, fm._countyID, fm._countiesList, fm._agencyID, fm._agencies, fm._fieldDivisionName, fm._groupName, fm._groupNameList, fm._stateName, fm._countyName, fm._countyNameList, fm._agencyName, fm._agencyNamesList);
    } else {
      let filterModel: GeographicFilterModel = new GeographicFilterModel(AppUtils.userFieldDivisionID(), fieldDivisionGroupID, fieldDivisionGroups, stateID, countyID, counties, agencyID, agencies, fieldDivisionName, groupName, groupNameList, stateName, countyName, countyNameList, agencyName, agencyNamesList);
      sessionStorage.setItem(storageKey, JSON.stringify(filterModel));
      return filterModel;
    }
  }

  public static embedDownloadLink(file: Blob, exportFilename: string): void {
    const link = document.createElement("a");
    const url = window.URL.createObjectURL(file);
    link.style.display = 'none';
    document.body.appendChild(link);
    if (link.download !== undefined) {
      link.setAttribute('href', url);
      link.setAttribute('download', exportFilename);
      link.click();
      window.URL.revokeObjectURL(url);
    }
    document.body.removeChild(link);
  }

  public static exportBlobToCsv(data: string, options?: any): void {
    let exportFilename: string = 'download';
    if (options && options.fileName) {
      exportFilename = options.fileName;
    }
    let blob = AppUtils.base64toBlob(data, 'text/csv;charset=utf-8;');
    this.embedDownloadLink(blob, exportFilename);
  }

  public static exportCSV(data: string, options?: any): void {
    let csv = '\ufeff'.concat('UNCLASSIFIED//LAW ENFORCEMENT SENSITIVE\r\n', data);
    let exportFilename: string = 'download';
    if (options && options.fileName) {
      exportFilename = options.fileName;
    }

    let blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });

    this.embedDownloadLink(blob, exportFilename);
  }

  public static formatAddress(address?: ServerModels.ENESS_Address | ServerModels.ENESS_AddressDTO): string | null {
    if (!address)
      return null;

    let stateAbbreviation: string = ('StateAbbreviation' in address) ? address.StateAbbreviation : address.ENESS_REF_State.StateAbbreviation;

    return `${address.StreetAddress || ''} ${address.Unit || ''}, ${address.CityName || ''}, ${stateAbbreviation || ''} ${address.PostalCode || ''}`;
  }

  public static formatEventAddress(address?: ServerModels.GunDetailEventAddressData): string | null {
    if (!address)
      return null;
    return `${address.StreetAddress || ''} ${address.Unit || ''}, ${address.CityName || ''}, ${address.StateAbbreviation || ''} ${address.PostalCode || ''}`;
  }

  public static setRequestHeaderToken(xhr: XMLHttpRequest) {
    xhr.setRequestHeader('Authorization', 'Bearer ' + sessionStorage.getItem(environment.authTokenStorageKey));
  }

  public static userCGICCode(): string {
    if (AccountComponent.userInfo && AccountComponent.userInfo.UserType) {
      if (AccountComponent.userInfo.Roles.findIndex(x => x.Name == <AuthRoles>'NESS Administrator') > -1 || AccountComponent.userInfo.UserType.UserTypeName == 'ATFHQ') {
        return 'all'
      }
      if (AccountComponent.userInfo.UserType.UserTypeName == 'ATFFieldDivision') {
        return AccountComponent.userInfo.FieldDivision.ENESS_REF_NIBINCGICCodes.OrganizationCode;
      }

      if (AccountComponent.userInfo.UserType.UserTypeName = 'StateLocalLE') {
        return null
      }
    }

    return null;
  }

  public static userFieldDivisionID(): string {
    if (AccountComponent.userInfo && AccountComponent.userInfo.UserType) {
      if (AccountComponent.userInfo.Roles.findIndex(x => x.Name == <AuthRoles>'NESS Administrator') > -1 || AccountComponent.userInfo.UserType.UserTypeName == 'ATFHQ') {
        return 'all'
      }
      if (AccountComponent.userInfo.UserType.UserTypeName == 'ATFFieldDivision') {
        return AccountComponent.userInfo.FieldDivision.FieldDivisionID;
      }

      if (AccountComponent.userInfo.UserType.UserTypeName = 'StateLocalLE') {
        return null
      }
    }

    return null;
  }

  public static objectIsEmpty(target: any): boolean {
    return Object.keys(target).reduce((res, k) => res && !(!!target[k] || target[k] === false || !isNaN(parseInt(target[k]))), true);
  }

  public static arrayGroupBy<T>(xs: T[], key: string): any {
    return xs.reduce(function (rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  };

  public static isEmptyInputValue(value: any): boolean {
    let isString: boolean = typeof (value) === 'string';
    // we don't check for string here so it also works with arrays
    return value == null || value.length === 0 || (isString && (<string>value).trim().length === 0);
  }

  public static getCurrentUserType(): string {
    return AccountComponent.userInfo.UserType.UserTypeName;
  }

  public static resolveFieldData(data: any, field: any): any {
    if (data && field) {
      if (ObjectUtils.isFunction(field)) {
        return field(data);
      } else if (field.indexOf('.') == -1) {
        return data[field];
      } else {
        let fields: string[] = field.split('.');
        let value = data;

        for (let i = 0; i < fields.length; i++) {
          if (value.hasOwnProperty(fields[i])) {
            value = value[fields[i]];

          } else if (Array.isArray(value)) {

            let childValue = [];
            for (let j = 0; j < value.length; j++) {
              if (value[j].hasOwnProperty(fields[i + 1])) {
                childValue.push(value[j][fields[i + 1]]);
              } else {
                childValue.push(value[j][fields[i]]);
              }
            }

            value = childValue || [];
          } else {
            value = typeof (value) === 'string' ? null : value.sort().join(' ');
          }
        }
        return value = typeof (value) === 'string' ? value : value.sort().join(' ');
      }
    } else {
      return null;
    }
  }

  public static multisortField(data1: any, data2: any, multiSortMeta: SortMeta[], index: number): number {
    let value1 = this.resolveFieldData(data1, multiSortMeta[index].field);
    let value2 = this.resolveFieldData(data2, multiSortMeta[index].field);
    let result = null;

    if (value1 == null && value2 != null)
      result = -1;
    else if (value1 != null && value2 == null)
      result = 1;
    else if (value1 == null && value2 == null)
      result = 0;
    else if (typeof value1 == 'string' || value1 instanceof String) {
      if (value1.localeCompare && (value1 != value2)) {
        return (multiSortMeta[index].order * value1.localeCompare(value2));
      }
    }
    else {
      result = (value1 < value2) ? -1 : 1;
    }

    if (value1 == value2) {
      return (multiSortMeta.length - 1) > (index) ? (this.multisortField(data1, data2, multiSortMeta, index + 1)) : 0;
    }

    return (multiSortMeta[index].order * result);
  }

  public static filterGlobal(this: Table): any {
    let globalFilterFieldsArray;
    if (this.filters['global']) {
      if (!this.columns && !this.globalFilterFields)
        throw new Error('Global filtering requires dynamic columns or globalFilterFields to be defined.');
      else
        globalFilterFieldsArray = this.globalFilterFields || this.columns;
    }

    this.filteredValue = [];

    for (let i = 0; i < this.value.length; i++) {
      let localMatch = true;
      let globalMatch = false;
      let localFiltered = false;

      for (let prop in this.filters) {
        if (this.filters.hasOwnProperty(prop) && prop !== 'global') {
          localFiltered = true;
          let filterField = prop;
          let filterMeta = this.filters[filterField];

          if (Array.isArray(filterMeta)) {
            for (let meta of filterMeta) {
              localMatch = this.executeLocalFilter(filterField, this.value[i], meta);

              if ((meta.operator === FilterOperator.OR && localMatch) || (meta.operator === FilterOperator.AND && !localMatch)) {
                break;
              }
            }
          }
          else {
            localMatch = this.executeLocalFilter(filterField, this.value[i], filterMeta);
          }

          if (!localMatch) {
            break;
          }
        }
      }

      if (this.filters['global'] && !globalMatch && globalFilterFieldsArray) {
        for (let j = 0; j < globalFilterFieldsArray.length; j++) {
          let globalFilterField = globalFilterFieldsArray[j].field || globalFilterFieldsArray[j];
          let fields = globalFilterField.split('.');
          if (fields.length > 0) {
            let childValue = this.value[i];
            for (let f = 0; f < fields.length; f++) {
              if (Array.isArray(childValue)) {
                childValue = childValue.map(p => p[fields[f]]);
              } else {
                childValue = childValue[fields[f]];
              }
            }
            globalMatch = this.filterService.filters[(<FilterMetadata>this.filters['global']).matchMode](childValue, (<FilterMetadata>this.filters['global']).value, this.filterLocale);
          } else {
            globalMatch = this.filterService.filters[(<FilterMetadata>this.filters['global']).matchMode](ObjectUtils.resolveFieldData(this.value[i], globalFilterField), (<FilterMetadata>this.filters['global']).value, this.filterLocale);
          }
          if (globalMatch) {
            break;
          }
        }
      }

      let matches: boolean;
      if (this.filters['global']) {
        matches = localFiltered ? (localFiltered && localMatch && globalMatch) : globalMatch;
      }
      else {
        matches = localFiltered && localMatch;
      }

      if (matches) {
        this.filteredValue.push(this.value[i]);
      }
    }

    if (this.filteredValue.length === this.value.length) {
      this.filteredValue = null;
    }

    if (this.paginator) {
      this.totalRecords = this.filteredValue ? this.filteredValue.length : this.value ? this.value.length : 0;
    }

    this.tableService.onValueChange(this.value);

    if (this.isStateful() && !this.restoringFilter) {
      this.saveState();
    }

    if (this.restoringFilter) {
      this.restoringFilter = false;
    }

    this.cd.markForCheck();

    if (this.scrollable) {
      this.resetScrollTop();
    }
  }

}
